// src/pages/app/BotDetailsPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Bot,
  BotChannel,
  getBotById,
  updateBot,
  fetchChannels,
  getBotPricingPreview,
  BotPricingPreview,
  MetaLeadAutomationSettings,
  fetchMetaLeadAutomation
} from "../../api/bots";
import { useTranslation } from "react-i18next";

const BotDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [bot, setBot] = useState<Bot | null>(null);
  const [channels, setChannels] = useState<BotChannel[] | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [form, setForm] = useState<{
    description: string;
    systemPrompt: string;
    autoEvaluateConversations: boolean;
  } | null>(null);

  const [pricing, setPricing] = useState<BotPricingPreview | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const nameEditRef = useRef<HTMLDivElement | null>(null);

  // NEW: meta lead automation (Lead Ads → WhatsApp)
  const [leadAutomation, setLeadAutomation] =
    useState<MetaLeadAutomationSettings | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setPricingError(null);

    Promise.all([getBotById(id), fetchChannels(id)])
      .then(([botData, channelData]) => {
        setBot(botData);
        setChannels(channelData || []);
        setForm({
          description: botData.description || "",
          systemPrompt: botData.systemPrompt,
          autoEvaluateConversations:
            botData.autoEvaluateConversations ?? false
        });

        // Load pricing + lead automation in parallel
        return Promise.all([
          getBotPricingPreview(botData.id),
          fetchMetaLeadAutomation(botData.id)
        ]);
      })
      .then(([pricingData, leadAutomationData]) => {
        setPricing(pricingData);
        setLeadAutomation(leadAutomationData);
      })
      .catch((err: any) => {
        console.error(err);
        setError(err?.message || t("botDetails.errors.loadBot"));
        setPricingError(t("botDetails.errors.loadPricing"));
      })
      .finally(() => setLoading(false));
  }, [id, t]);

  useEffect(() => {
    if (bot?.name) {
      setNameDraft(bot.name);
    }
  }, [bot?.name]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleChange =
    (field: keyof NonNullable<typeof form>) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!form) return;
      const value = e.target.value;
      setForm({
        ...form,
        [field]: value
      });
    };

  const handleSave: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (!id || !form) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updated = await updateBot(id, {
        description: form.description,
        systemPrompt: form.systemPrompt,
        autoEvaluateConversations: form.autoEvaluateConversations
      });

      setBot(updated);
      setSuccess(t("botDetails.success.basicsUpdated"));
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botDetails.errors.updateBot"));
    } finally {
      setSaving(false);
    }
  };

  const handleStartEditName = () => {
    if (!bot) return;
    setNameDraft(bot.name);
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setNameDraft(bot?.name ?? "");
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    if (!id || !bot) return;
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setError(
        t("botDetails.errors.nameRequired", "Bot name is required.")
      );
      return;
    }
    if (trimmed === bot.name) {
      setIsEditingName(false);
      return;
    }

    setSavingName(true);
    setError(null);
    setSuccess(null);

    try {
      const updated = await updateBot(id, { name: trimmed });
      setBot(updated);
      setSuccess(
        t("botDetails.success.nameUpdated", "Bot name updated.")
      );
      setIsEditingName(false);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botDetails.errors.updateBot"));
    } finally {
      setSavingName(false);
    }
  };

  const handleNameBlur: React.FocusEventHandler<HTMLInputElement> = (
    e
  ) => {
    if (savingName || !isEditingName) return;
    const next = e.relatedTarget as Node | null;
    if (next && nameEditRef.current?.contains(next)) {
      return;
    }
    handleSaveName();
  };

  if (!id) {
    return (
      <div className="page-container">
        <p>{t("botDetails.missingId")}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container">
        <p>{t("botDetails.loading")}</p>
      </div>
    );
  }

  if (error && !bot) {
    return (
      <div className="page-container">
        <h1>{t("botDetails.errorTitle")}</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (!bot || !form) {
    return (
      <div className="page-container">
        <h1>{t("botDetails.notFoundTitle")}</h1>
      </div>
    );
  }

  const isActive = bot.status === "ACTIVE";

  // ---- STATUS LOGIC ----

  // Knowledge
  const knowledgeEnabled = bot.useDomainCrawler || bot.usePdfCrawler;
  const knowledgeInitialized = knowledgeEnabled && !!bot.knowledgeClientId;

  // Channels
  const channelsArray = channels || [];
  const selectedChannels = {
    web: bot.channelWeb,
    whatsapp: bot.channelWhatsapp,
    messenger: bot.channelMessenger,
    instagram: bot.channelInstagram
  };

  const webConfigured = !bot.channelWeb || !!bot.domain;
  const whatsappConfigured =
    !bot.channelWhatsapp ||
    channelsArray.some(
      (c) => c.type === "WHATSAPP" && !!c.externalId?.trim()
    );
  const messengerConfigured =
    !bot.channelMessenger ||
    channelsArray.some(
      (c) => c.type === "FACEBOOK" && !!c.externalId?.trim()
    );
  const instagramConfigured =
    !bot.channelInstagram ||
    channelsArray.some(
      (c) => c.type === "INSTAGRAM" && !!c.externalId?.trim()
    );

  const channelsEnabledCount = Object.values(selectedChannels).filter(Boolean)
    .length;

  const allSelectedChannelsConfigured =
    webConfigured &&
    whatsappConfigured &&
    messengerConfigured &&
    instagramConfigured;

  const missingChannelConfigs: string[] = [];
  if (bot.channelWeb && !webConfigured)
    missingChannelConfigs.push(
      t("botDetails.channels.missing.webDomain")
    );
  if (bot.channelWhatsapp && !whatsappConfigured)
    missingChannelConfigs.push(
      t("botDetails.channels.missing.whatsappExternalId")
    );
  if (bot.channelMessenger && !messengerConfigured)
    missingChannelConfigs.push(
      t("botDetails.channels.missing.facebookPageId")
    );
  if (bot.channelInstagram && !instagramConfigured)
    missingChannelConfigs.push(
      t("botDetails.channels.missing.instagramBusinessId")
    );

  // Calendar
  const calendarEnabled = bot.useCalendar;
  const calendarConfigured =
    calendarEnabled &&
    !!bot.calendarId &&
    !!bot.timeZone &&
    !!bot.defaultDurationMinutes;

  // NEW: Lead Ads / Meta leads feature flags
  const hasLeadAdsFeature =
    bot.leadWhatsappMessages200 ||
    bot.leadWhatsappMessages500 ||
    bot.leadWhatsappMessages1000;

  // NEW: WhatsApp + Facebook connection for Lead Ads
  const whatsappChannel = channelsArray.find(
    (c) => c.type === "WHATSAPP"
  );
  const facebookChannel = channelsArray.find(
    (c) => c.type === "FACEBOOK"
  );

  const whatsappChannelMeta = (whatsappChannel?.meta as any) || {};
  const facebookChannelMeta = (facebookChannel?.meta as any) || {};

  const whatsappLeadChannelConnected =
    !!whatsappChannel &&
    !!whatsappChannel.externalId?.trim() &&
    whatsappChannelMeta.needsReconnect !== true;

  const facebookLeadChannelConnected =
    !!facebookChannel &&
    !!facebookChannel.externalId?.trim() &&
    facebookChannelMeta.needsReconnect !== true;

  // NEW: Lead automation is considered "present" when we have a template configured
  const hasLeadAutomation =
    !!leadAutomation &&
    !!leadAutomation.templateName &&
    !!leadAutomation.templateLanguage;

  const leadAdsEnabled = hasLeadAdsFeature;
  const leadAdsFullyConfigured =
    leadAdsEnabled &&
    whatsappLeadChannelConnected &&
    facebookLeadChannelConnected &&
    hasLeadAutomation;

  const missingLeadAdsBits: string[] = [];
  if (!whatsappLeadChannelConnected) {
    missingLeadAdsBits.push(
      t("botDetails.health.leadAds.missingWhatsapp", "WhatsApp channel not connected")
    );
  }
  if (!facebookLeadChannelConnected) {
    missingLeadAdsBits.push(
      t("botDetails.health.leadAds.missingFacebook", "Facebook channel not connected")
    );
  }
  if (!hasLeadAutomation) {
    missingLeadAdsBits.push(
      t(
        "botDetails.health.leadAds.missingAutomation",
        "Lead Ads automation not configured"
      )
    );
  }

  const badgeClass = (kind: "ok" | "warn" | "error") => {
    if (kind === "ok") return "status-badge status-badge-ok";
    if (kind === "warn") return "status-badge status-badge-warn";
    return "status-badge status-badge-error";
  };

  const getBotStatusBadgeKind = (
    status: Bot["status"]
  ): "ok" | "warn" | "error" => {
    const normalized = status.toUpperCase();
    if (normalized === "ACTIVE") return "ok";
    if (normalized === "DRAFT") return "warn";
    return "error";
  };

  const getStatusPillClass = (status: Bot["status"]) => {
    const normalized = status.toUpperCase();
    if (normalized === "ACTIVE")
      return "plan-summary-status plan-summary-status-ok";
    if (normalized === "DRAFT")
      return "plan-summary-status plan-summary-status-warn";
    if (normalized === "CANCELLED" || normalized === "INACTIVE")
      return "plan-summary-status plan-summary-status-error";
    return "plan-summary-status";
  };

  const statusPillClass = getStatusPillClass(bot.status);

  const enabledChannelNames = [
    bot.channelWeb ? t("botDetails.channels.names.web") : null,
    bot.channelWhatsapp ? t("botDetails.channels.names.whatsapp") : null,
    bot.channelMessenger ? t("botDetails.channels.names.messenger") : null,
    bot.channelInstagram ? t("botDetails.channels.names.instagram") : null
  ].filter(Boolean) as string[];

  return (
    <div>
      {/* HEADER / HERO */}
      <div className="page-header">
        <div className="bot-header">
          <div className="bot-avatar">
            {bot.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="bot-title-row">
              {!isEditingName ? (
                <>
                  <h1 className="bot-title">{bot.name}</h1>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={handleStartEditName}
                    aria-label={t(
                      "botDetails.actions.editName",
                      "Edit bot name"
                    )}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M4 20h4l10-10-4-4L4 16v4zM14 6l4 4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </>
              ) : (
                <div className="bot-title-edit" ref={nameEditRef}>
                  <input
                    className="bot-title-input"
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    ref={nameInputRef}
                    onBlur={handleNameBlur}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSaveName();
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        handleCancelEditName();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="icon-button icon-button--primary"
                    onClick={handleSaveName}
                    disabled={savingName}
                    aria-label={t(
                      "botDetails.actions.saveName",
                      "Save bot name"
                    )}
                  >
                    {savingName ? (
                      <span className="icon-button-spinner" aria-hidden="true" />
                    ) : (
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path
                          d="M5 12l4 4 10-10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={handleCancelEditName}
                    aria-label={t(
                      "botDetails.actions.cancelEditName",
                      "Cancel"
                    )}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        d="M6 6l12 12M18 6l-12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
            <p className="muted">
              {t("botDetails.slugLabel")} <code>{bot.slug}</code>
            </p>
          </div>
        </div>

        <div className="page-header-actions">
          <span className={statusPillClass}>{bot.status}</span>

          <Link
            to={`/demo/${bot.slug}`}
            className="btn-secondary"
            target="_blank"
          >
            {t("botDetails.actions.openDemo")}
          </Link>

          {!isActive && (
            <Link
              to={`/app/bots/${bot.id}/plan`}
              className="btn-primary"
            >
              {t("botDetails.actions.activatePay")}
            </Link>
          )}

          {isActive && (
            <Link
              to={`/app/bots/${bot.id}/plan`}
              className="btn-primary"
            >
              {t("botDetails.actions.viewPlanBilling")}
            </Link>
          )}
        </div>
      </div>

      <div className="detail-layout">
        {/* LEFT */}
        <section className="detail-main">
          <h2>{t("botDetails.basics.title")}</h2>
          <p className="muted" style={{ marginTop: "0.25rem" }}>
            {t("botDetails.basics.subtitle")}
          </p>

          <form className="form" onSubmit={handleSave}>
            {error && <div className="form-error">{error}</div>}
            {success && <div className="form-success">{success}</div>}

            <label className="form-field">
              <span>{t("botDetails.basics.descriptionLabel")}</span>
              <textarea
                value={form.description}
                onChange={handleChange("description")}
                rows={2}
                placeholder={t(
                  "botDetails.basics.descriptionPlaceholder"
                )}
              />
            </label>

            <label className="form-field">
              <span>{t("botDetails.basics.systemPromptLabel")}</span>
              <textarea
                value={form.systemPrompt}
                onChange={handleChange("systemPrompt")}
                rows={5}
                placeholder={t(
                  "botDetails.basics.systemPromptPlaceholder"
                )}
              />
              <span
                style={{
                  fontSize: "0.8rem",
                  marginTop: "0.1rem"
                }}
              >
                {t("botDetails.basics.systemPromptHelp")}
              </span>
            </label>

            <button
              className="btn-primary"
              type="submit"
              disabled={saving}
            >
              {saving
                ? t("botDetails.actions.saving")
                : t("botDetails.actions.saveBasics")}
            </button>
          </form>

          {/* HEALTH OVERVIEW */}
          <h2 style={{ marginTop: "2rem" }}>
            {t("botDetails.health.title")}
          </h2>
          <div className="status-overview">
            {/* Bot status */}
            <div className="status-row">
              <div className="status-row-header">
                <span className="status-label">
                  {t("botDetails.health.botStatusTitle")}
                </span>
                <span
                  className={badgeClass(
                    getBotStatusBadgeKind(bot.status)
                  )}
                >
                  {bot.status}
                </span>
              </div>

              <p className="muted">
                {bot.status === "ACTIVE"
                  ? t("botDetails.health.botStatus.active")
                  : bot.status === "DRAFT"
                  ? t("botDetails.health.botStatus.draft")
                  : t("botDetails.health.botStatus.inactive")}
              </p>
            </div>

            {/* Knowledge status */}
            <div className="status-row">
              <div className="status-row-header">
                <span className="status-label">
                  {t("botDetails.health.knowledgeTitle")}
                </span>
                {knowledgeEnabled ? (
                  knowledgeInitialized ? (
                    <span className={badgeClass("ok")}>
                      {t("botDetails.health.badges.configured")}
                    </span>
                  ) : (
                    <span className={badgeClass("warn")}>
                      {t(
                        "botDetails.health.badges.enabledNotInitialized"
                      )}
                    </span>
                  )
                ) : (
                  <span className={badgeClass("error")}>
                    {t("botDetails.health.badges.disabled")}
                  </span>
                )}
              </div>

              <p className="muted">
                {!knowledgeEnabled
                  ? t("botDetails.health.knowledge.disabled")
                  : !knowledgeInitialized
                  ? t(
                      "botDetails.health.knowledge.enabledNotInitialized"
                    )
                  : t("botDetails.health.knowledge.configured")}
              </p>
            </div>

            {/* Channels status */}
            <div className="status-row">
              <div className="status-row-header">
                <span className="status-label">
                  {t("botDetails.health.channelsTitle")}
                </span>
                {channelsEnabledCount === 0 ? (
                  <span className={badgeClass("warn")}>
                    {t(
                      "botDetails.health.badges.noChannelsEnabled"
                    )}
                  </span>
                ) : allSelectedChannelsConfigured ? (
                  <span className={badgeClass("ok")}>
                    {t(
                      "botDetails.health.badges.allChannelsConfigured"
                    )}
                  </span>
                ) : (
                  <span className={badgeClass("warn")}>
                    {t(
                      "botDetails.health.badges.someChannelsNeedSetup"
                    )}
                  </span>
                )}
              </div>

              <p className="muted">
                {channelsEnabledCount === 0 && (
                  <>
                    {t("botDetails.health.channels.none")}{" "}
                    <Link to={`/app/bots/${bot.id}/features`}>
                      {t("botDetails.nav.featuresPlan")}
                    </Link>{" "}
                    {t("botDetails.health.channels.and")}{" "}
                    <Link to={`/app/bots/${bot.id}/channels`}>
                      {t("botDetails.nav.channels")}
                    </Link>
                    .
                  </>
                )}

                {channelsEnabledCount > 0 &&
                  allSelectedChannelsConfigured && (
                    <>
                      {t(
                        "botDetails.health.channels.enabledList",
                        {
                          channels: enabledChannelNames.join(", ")
                        }
                      )}
                    </>
                  )}

                {channelsEnabledCount > 0 &&
                  !allSelectedChannelsConfigured && (
                    <>
                      {t(
                        "botDetails.health.channels.missingConfig",
                        {
                          missing: missingChannelConfigs.join(", ")
                        }
                      )}{" "}
                      <Link to={`/app/bots/${bot.id}/channels`}>
                        {t("botDetails.nav.channels")}
                      </Link>
                      .
                    </>
                  )}
              </p>
            </div>

            {/* NEW: Lead Ads / Meta leads status */}
            <div className="status-row">
              <div className="status-row-header">
                <span className="status-label">
                  {t(
                    "botDetails.health.leadAdsTitle",
                    "Lead Ads → WhatsApp"
                  )}
                </span>
                {!leadAdsEnabled ? (
                  <span className={badgeClass("error")}>
                    {t("botDetails.health.badges.disabled")}
                  </span>
                ) : leadAdsFullyConfigured ? (
                  <span className={badgeClass("ok")}>
                    {t("botDetails.health.badges.configured")}
                  </span>
                ) : (
                  <span className={badgeClass("warn")}>
                    {t(
                      "botDetails.health.badges.enabledNotConfigured"
                    )}
                  </span>
                )}
              </div>

              <p className="muted">
                {!leadAdsEnabled ? (
                  t(
                    "botDetails.health.leadAds.disabled",
                    "Lead Ads → WhatsApp add-on is not enabled. Enable it from the Features & plan page to automatically message new leads."
                  )
                ) : leadAdsFullyConfigured ? (
                  t(
                    "botDetails.health.leadAds.configured",
                    "Lead Ads → WhatsApp is fully configured: Facebook page, WhatsApp number and automation rule are set."
                  )
                ) : (
                  <>
                    {t(
                      "botDetails.health.leadAds.enabledNotConfigured",
                      "Lead Ads → WhatsApp is enabled but some pieces still need configuration:"
                    )}{" "}
                    {missingLeadAdsBits.join(", ")}.{" "}
                    <Link
                      to={`/app/bots/${bot.id}/whatsapp-templates`}
                    >
                      {t(
                        "botDetails.health.leadAds.goToSettings",
                        "Go to Ads settings"
                      )}
                    </Link>
                    .
                  </>
                )}
              </p>
            </div>

            {/* Calendar status */}
            <div className="status-row">
              <div className="status-row-header">
                <span className="status-label">
                  {t("botDetails.health.calendarTitle")}
                </span>
                {!calendarEnabled ? (
                  <span className={badgeClass("error")}>
                    {t("botDetails.health.badges.disabled")}
                  </span>
                ) : calendarConfigured ? (
                  <span className={badgeClass("ok")}>
                    {t("botDetails.health.badges.configured")}
                  </span>
                ) : (
                  <span className={badgeClass("warn")}>
                    {t(
                      "botDetails.health.badges.enabledNotConfigured"
                    )}
                  </span>
                )}
              </div>

              <p className="muted">
                {!calendarEnabled
                  ? t("botDetails.health.calendar.disabled")
                  : !calendarConfigured
                  ? t(
                      "botDetails.health.calendar.enabledNotConfigured"
                    )
                  : t("botDetails.health.calendar.configured", {
                      calendarId: bot.calendarId,
                      timeZone: bot.timeZone,
                      duration: bot.defaultDurationMinutes || 30
                    })}
              </p>
            </div>
          </div>
        </section>

        {/* RIGHT */}
        <section className="detail-side">
          <h2>{t("botDetails.workspace.title")}</h2>
          <p className="muted" style={{ marginTop: "0.25rem" }}>
            {t("botDetails.workspace.subtitle")}
          </p>

          <ul
            className="bot-nav-list"
            style={{ marginTop: "1.25rem" }}
          >
            <li>
              <Link
                to={`/app/bots/${bot.id}/features`}
                className="bot-nav-item"
              >
                <div className="bot-nav-item-main">
                  <span className="bot-nav-item-title">
                    {t("botDetails.nav.featuresPlan")}
                  </span>
                  <span className="bot-nav-item-description">
                    {t("botDetails.nav.featuresPlanDesc")}
                  </span>
                </div>
                <span className="bot-nav-item-arrow">→</span>
              </Link>
            </li>

            <li>
              <Link
                to={`/app/bots/${bot.id}/knowledge`}
                className="bot-nav-item"
              >
                <div className="bot-nav-item-main">
                  <span className="bot-nav-item-title">
                    {t("botDetails.nav.knowledge")}
                  </span>
                  <span className="bot-nav-item-description">
                    {t("botDetails.nav.knowledgeDesc")}
                  </span>
                </div>
                <span className="bot-nav-item-arrow">→</span>
              </Link>
            </li>

            <li>
              <Link
                to={`/app/bots/${bot.id}/channels`}
                className="bot-nav-item"
              >
                <div className="bot-nav-item-main">
                  <span className="bot-nav-item-title">
                    {t("botDetails.nav.channels")}
                  </span>
                  <span className="bot-nav-item-description">
                    {t("botDetails.nav.channelsDesc")}
                  </span>
                </div>
                <span className="bot-nav-item-arrow">→</span>
              </Link>
            </li>

            <li>
              <Link
                to={`/app/bots/${bot.id}/conversations`}
                className="bot-nav-item"
              >
                <div className="bot-nav-item-main">
                  <span className="bot-nav-item-title">
                    {t("botDetails.nav.conversations")}
                  </span>
                  <span className="bot-nav-item-description">
                    {t("botDetails.nav.conversationsDesc")}
                  </span>
                </div>
                <span className="bot-nav-item-arrow">→</span>
              </Link>
            </li>
            <li>
              <Link
                to={`/app/bots/${bot.id}/settings`}
                className="bot-nav-item"
              >
                <div className="bot-nav-item-main">
                  <span className="bot-nav-item-title">
                    {t("botDetails.nav.settings")}
                  </span>
                  <span className="bot-nav-item-description">
                    {t("botDetails.nav.settingsDesc")}
                  </span>
                </div>
                <span className="bot-nav-item-arrow">→</span>
              </Link>
            </li>
            <li>
              <Link
                to={`/app/bots/${bot.id}/whatsapp-templates`}
                className="bot-nav-item"
              >
                <div className="bot-nav-item-main">
                  <span className="bot-nav-item-title">
                    {t("botDetails.nav.adsSettings")}
                  </span>
                  <span className="bot-nav-item-description">
                    {t("botDetails.nav.adsSettingsDesc")}
                  </span>
                </div>
                <span className="bot-nav-item-arrow">→</span>
              </Link>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default BotDetailPage;
